import React from 'react';
import Icon from '@/components/ui/AppIcon';

const ConversationPreparation: React.FC = () => {
  const preparationSteps = [
    {
      icon: 'DocumentTextIcon',
      title: 'Review Your Strategic Context',
      description: 'Prepare a brief overview of your institutional mandate, current strategic priorities, and key transformation challenges you are facing.',
    },
    {
      icon: 'UserGroupIcon',
      title: 'Identify Key Stakeholders',
      description: 'Consider who within your organization should be involved in strategic advisory discussions and their primary concerns.',
    },
    {
      icon: 'ChartBarIcon',
      title: 'Define Success Indicators',
      description: 'Think about what successful transformation would look like for your institution and how you would measure progress.',
    },
    {
      icon: 'CalendarIcon',
      title: 'Consider Timeline Constraints',
      description: 'Reflect on any organizational, political, or budgetary timelines that might influence your transformation approach.',
    },
  ];

  const discussionTopics = [
    'Your institutional transformation objectives and constraints',
    'Current organizational capabilities and capacity gaps',
    'Leadership development and executive capacity needs',
    'Strategic planning and execution challenges',
    'Our systematic approach and methodology',
    'Engagement models and timeline considerations',
    'Next steps and mutual fit assessment',
  ];

  return (
    <div className="bg-white rounded-lg institutional-shadow p-5 sm:p-8 lg:p-12">
      <div className="mb-8 sm:mb-12">
        <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-text-primary mb-3 sm:mb-4">
          Preparing for Our Initial Conversation
        </h2>
        <p className="text-sm sm:text-base text-text-secondary leading-relaxed">
          To make our initial discussion as productive as possible, we recommend considering the following areas. This preparation helps ensure we can provide relevant insights and determine if there is a strong mutual fit for collaboration.
        </p>
      </div>

      <div className="mb-8 sm:mb-12">
        <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-4 sm:mb-6 institutional-accent-border pl-4">
          Preparation Considerations
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          {preparationSteps.map((step, index) => (
            <div key={index} className="flex items-start gap-3 sm:gap-4">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-institutional/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name={step.icon as any} size={18} className="text-institutional" />
              </div>
              <div>
                <h4 className="font-semibold text-text-primary mb-1 sm:mb-2 text-sm sm:text-base">{step.title}</h4>
                <p className="text-xs sm:text-sm text-text-secondary leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-8 sm:mb-12">
        <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-4 sm:mb-6 institutional-accent-border pl-4">
          What to Expect from Our Discussion
        </h3>
        <div className="bg-surface rounded-lg p-4 sm:p-6">
          <p className="text-sm sm:text-base text-text-secondary mb-3 sm:mb-4 leading-relaxed">
            Our initial conversation is designed to be exploratory and substantive. We will discuss:
          </p>
          <ul className="space-y-2 sm:space-y-3">
            {discussionTopics.map((topic, index) => (
              <li key={index} className="flex items-start gap-2 sm:gap-3">
                <Icon
                  name="CheckCircleIcon"
                  size={18}
                  className="text-success flex-shrink-0 mt-0.5"
                  variant="solid"
                />
                <span className="text-xs sm:text-sm text-text-secondary">{topic}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="bg-institutional/5 rounded-lg p-4 sm:p-6 institutional-border">
        <div className="flex items-start gap-3 sm:gap-4">
          <Icon name="LightBulbIcon" size={22} className="text-institutional flex-shrink-0 mt-1" />
          <div>
            <h4 className="font-semibold text-text-primary mb-2 text-sm sm:text-base">
              Advisory Team Engagement Approach
            </h4>
            <p className="text-xs sm:text-sm text-text-secondary leading-relaxed">
              All initial conversations are conducted by our senior advisory team. This ensures you receive direct access to senior strategic thinking and allows us to properly assess whether our systematic approach aligns with your institutional needs. We believe in collaborative partnerships built on mutual commitment to long-term transformation success.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversationPreparation;